<script lang="ts" setup>
export interface ButtonBaseProps {
  to?: string | object
  href?: string
  disabled?: boolean
}
defineProps<ButtonBaseProps>()
</script>

<template>
  <NuxtLink v-if="to || href" :to="to" :href="href" :class="['button', { disabled }]">
    <span class="background" />
    <span class="text">
      <slot name="before" />
      <slot> Button </slot>
      <slot name="after" />
    </span>
  </NuxtLink>
  <button v-else :class="['button', { disabled }]">
    <span class="background" />
    <span class="text">
      <slot name="before" />
      <slot> Button </slot>
      <slot name="after" />
    </span>
  </button>
</template>

<style lang="scss" scoped>
.button {
  --color: var(--c-primary);
  --color-hover: var(--c-blue-rollover);
  --color-background: transparent;
  --color-background-hover: var(--color-background);
  --color-border: var(--c-blue-60);
  --color-border-hover: var(--c-blue-rollover);
  --gap: 12px;

  --icon-size: 18px;

  position: relative;
  padding: var(--pv) var(--ph);
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  @include fluid-ts-ds(--pv, 18px, 24px);
  @include fluid-ts-ds(--ph, 24px, 32px);
  @include hover {
    color: var(--color-hover);

    :deep(.background) {
      border-width: 2px;
      border-color: var(--color-border-hover);
      background-color: var(--color-background-hover);
      transform: scale(0.92);
    }
    :deep(.text) {
      color: var(--color-hover);
    }
  }

  &.disabled,
  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
  }

  :slotted(*) {
    display: grid;
    place-content: center;
    width: var(--icon-size);
    height: var(--icon-size);
  }
}

.background {
  content: '';
  @include fill;
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  @include transitions(transform, background-color, border-color, border-width);
}

.text {
  position: relative;
  display: flex;
  align-items: center;
  color: var(--color);
  @include transitions(color);
}

.slide {
  --amount: calc(var(--icon-size) + var(--gap));
  .text {
    @include transitions(transform);
  }
  :deep(svg) {
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    @include transitions(opacity);
  }

  &-right {
    .text {
      padding-right: var(--amount);
    }
    :deep(svg:first-child) {
      opacity: 0;
      right: calc(100% + var(--gap));
    }
    :deep(svg:last-child) {
      right: 0;
    }

    @include hover {
      .text {
        transform: translate(var(--amount), 0);
      }
      :deep(svg:first-child) {
        opacity: 1;
      }
      :deep(svg:last-child) {
        opacity: 0;
      }
    }
  }
  &-left {
    .text {
      padding-left: var(--amount);
    }
    :deep(svg:first-child) {
      left: 0;
    }
    :deep(svg:last-child) {
      opacity: 0;
      right: calc(-1 * var(--amount));
    }

    @include hover {
      .text {
        transform: translate(calc(-1 * var(--amount)), 0);
      }
      :deep(svg:first-child) {
        opacity: 0;
      }
      :deep(svg:last-child) {
        opacity: 1;
      }
    }
  }
}
</style>
